// A retry-capable `fetch` for every generated SDK client — luna-api,
// vellum-api, customer-platform-api — and any hand-rolled caller alongside them.
// Lives here rather than in one SDK package so no app has to depend on another
// app's client to get it; `apiError.ts` next door is its counterpart for reading
// the error bodies that do come back.
//
// Field nurses work on flaky mobile connections, so a rejected `fetch()` is
// usually a connectivity blip rather than a platform failure: we retry
// idempotent requests a couple of times and, if that doesn't help, hand back a
// synthetic response that says so instead of a fabricated 500 (PEAK-456).

// Shown to the user once retries are exhausted. Deliberately connection-flavored
// — the previous "Internal server error, please try again soon or contact
// support." blamed the platform for the phone's dropped signal.
export const NETWORK_ERROR_MESSAGE =
  "Couldn't reach the server — check your connection and try again.";

// A real server error and our synthetic one need to be told apart by callers, so
// the synthetic body carries `network_error: true` and the response carries a
// marker header. 503 (rather than the old 500) keeps "the request never landed"
// distinct from "the server errored" for anything branching on status.
export const NETWORK_ERROR_STATUS = 503;
const NETWORK_ERROR_HEADER = "x-luna-network-error";

// Only requests that can be safely replayed are retried. A rejected POST/PUT/
// PATCH/DELETE may already have been applied server-side (double job-accept,
// double procedure-create), and the browser gives us no way to tell.
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD"]);

// Three attempts total, ~500ms then ~1.5s apart. Long enough to ride out a
// handoff between cell towers, short enough that a genuinely offline phone
// isn't left staring at a spinner.
const RETRY_DELAYS_MS = [500, 1500];
const JITTER = 0.25;

export type NetworkErrorBody = {
  detail: string;
  network_error: true;
};

/** Context handed to `onNetworkError`. Safe to attach to an error report as-is. */
export type NetworkErrorContext = {
  /** Request path only — query strings can carry patient identifiers. */
  path: string;
  method: string;
  attempts: number;
};

export type RetryingFetchOptions = {
  /**
   * Called once per unrecoverable network failure, after the last retry. Each
   * app wires its own reporter here (the lib stays free of a Sentry
   * dependency, since consumers run on different Sentry SDKs). Callers must not
   * report the synthetic response again downstream.
   */
  onNetworkError?: (error: unknown, context: NetworkErrorContext) => void;
  /** Overrides the default `globalThis.fetch`, mainly for tests. */
  fetchImpl?: typeof fetch;
};

/**
 * Thrown by callers that turn a failed response into an exception, so a
 * connectivity blip stays distinguishable from a real server error all the way
 * up the stack. Already reported via `onNetworkError` — don't report it again.
 */
export class NetworkError extends Error {
  readonly network_error = true;

  constructor(message: string = NETWORK_ERROR_MESSAGE) {
    super(message);
    this.name = "NetworkError";
  }
}

/**
 * True for the synthetic body produced when a request never reached the server,
 * and for the `NetworkError` thrown from it. Lets UI surfaces offer a "try
 * again" affordance instead of a full error screen, and keeps duplicate error
 * reports out of downstream handling.
 */
export function isNetworkError(bodyOrError: unknown): boolean {
  return (
    typeof bodyOrError === "object" &&
    bodyOrError !== null &&
    (bodyOrError as { network_error?: unknown }).network_error === true
  );
}

/** True for the synthetic response returned after retries are exhausted. */
export function isNetworkErrorResponse(response: Response): boolean {
  return response.headers.get(NETWORK_ERROR_HEADER) === "1";
}

export function networkErrorBody(): NetworkErrorBody {
  return { detail: NETWORK_ERROR_MESSAGE, network_error: true };
}

function networkErrorResponse(): Response {
  return new Response(JSON.stringify(networkErrorBody()), {
    status: NETWORK_ERROR_STATUS,
    headers: {
      "Content-Type": "application/json",
      [NETWORK_ERROR_HEADER]: "1",
    },
  });
}

// An aborted request isn't a failure worth retrying or reporting: the caller
// (or a navigation away from the page) asked for it to stop, and the signal
// would reject every retry instantly anyway. It is rethrown as-is rather than
// turned into a connection-error response — a deliberate cancellation is not a
// dropped connection, and callers filter it with `err.name === 'AbortError'`.
function isAbortError(error: unknown): boolean {
  return (error as { name?: string } | undefined)?.name === "AbortError";
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  const method =
    init?.method ?? (input instanceof Request ? input.method : "GET");
  return method.toUpperCase();
}

// Query strings carry patient identifiers (e.g. `?patient_name=`), so only the
// path is ever handed to the error reporter.
function requestPath(input: RequestInfo | URL): string {
  const raw =
    input instanceof Request
      ? input.url
      : typeof input === "string"
        ? input
        : input.toString();
  return raw.split("?")[0];
}

function delay(ms: number): Promise<void> {
  const jittered = ms * (1 + (Math.random() * 2 - 1) * JITTER);
  return new Promise((resolve) => setTimeout(resolve, jittered));
}

/**
 * Builds a `fetch` that retries network-level failures on idempotent requests
 * and, when they don't recover, resolves to a synthetic connection-error
 * response instead of rejecting. Only the `fetch()` promise rejecting is
 * retried — anything that reached the server (a real 500, a WAF 403, any other
 * status) is returned untouched on the first attempt. An abort still rejects,
 * as `fetch` itself would.
 *
 * Pass it to the generated client via `client.setConfig({ fetch })`, or call it
 * directly in place of `fetch`.
 */
export function createRetryingFetch({
  onNetworkError,
  fetchImpl,
}: RetryingFetchOptions = {}): typeof fetch {
  return async (input, init) => {
    const doFetch = fetchImpl ?? globalThis.fetch;
    const method = requestMethod(input, init);
    const attempts = IDEMPOTENT_METHODS.has(method)
      ? RETRY_DELAYS_MS.length + 1
      : 1;

    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        // A Request is consumed by fetch, so each retry gets its own copy.
        const target =
          attempt > 0 && input instanceof Request ? input.clone() : input;
        return await doFetch(target, init);
      } catch (error) {
        const signal =
          init?.signal ?? (input instanceof Request ? input.signal : null);
        if (isAbortError(error) || signal?.aborted) {
          throw error;
        }

        const lastAttempt = attempt === attempts - 1;
        if (lastAttempt) {
          onNetworkError?.(error, {
            path: requestPath(input),
            method,
            attempts: attempt + 1,
          });
          return networkErrorResponse();
        }

        await delay(RETRY_DELAYS_MS[attempt]!);
      }
    }

    // Unreachable: the loop either returns a response or the synthetic one.
    return networkErrorResponse();
  };
}
